import { logError } from '../shared/utils/logger';
import {
    getTokens as readTokens,
    setTokens as persistTokens,
    clearTokens,
} from '../shared/utils/secureTokenStorage';

// Organization context for multi-tenant API requests
let currentOrgId = null;

/**
 * Set the current organization context for API requests
 * This will add X-Org-Id header to all authenticated requests
 *
 * @param {string|null} orgId - The organization ID, or null to clear
 */
export const setOrgContext = (orgId) => {
    currentOrgId = orgId;
};

/**
 * Get the current organization context
 *
 * @returns {string|null} The current organization ID
 */
export const getOrgContext = () => {
    return currentOrgId;
};

/**
 * Clear the current organization context
 */
export const clearOrgContext = () => {
    currentOrgId = null;
};

// Prefer same-origin (proxy) by default; allow explicit override via REACT_APP_API_BASE.
// If REACT_APP_API_BASE is set, use it in both development and production.
// Otherwise, use empty string to leverage the package.json proxy.
const RAW_BASE_URL = process.env.REACT_APP_API_BASE || "";
const normalizedBaseUrl = RAW_BASE_URL.replace(/\/+$/, "");
export const BASE_URL = normalizedBaseUrl;

// Global authentication error handler
let globalAuthErrorHandler = null;

/**
 * Register a global callback to handle authentication errors
 * This will be called when authentication permanently fails (e.g., token refresh fails)
 */
export const setGlobalAuthErrorHandler = (handler) => {
    globalAuthErrorHandler = handler;
};

/**
 * Clear the global authentication error handler
 */
export const clearGlobalAuthErrorHandler = () => {
    globalAuthErrorHandler = null;
};


export const getStoredTokens = () => readTokens();

// Backward compatibility helper
export const normalizeTokens = (tokenData) => {
    // Handle different backend response formats
    if (!tokenData) return null;

    // If tokenData is a string, assume it's the access token
    if (typeof tokenData === "string") {
        return { accessToken: tokenData };
    }

    // If tokenData already has accessToken, use as-is
    if (tokenData.accessToken) {
        return tokenData;
    }

    // Handle snake_case access_token (common in some backends)
    if (tokenData.access_token) {
        return {
            accessToken: tokenData.access_token,
            refreshToken: tokenData.refresh_token || tokenData.refreshToken
        };
    }

    // If tokenData has 'token' property (nested structure)
    if (tokenData.token) {
        if (typeof tokenData.token === "string") {
            return { accessToken: tokenData.token, refreshToken: tokenData.refreshToken };
        }
        return tokenData.token;
    }

    // Fallback: assume the object itself contains the token data
    return tokenData;
};

export async function refreshAccessToken() {
    const tokens = readTokens();
    if (!tokens?.refreshToken) {
        clearTokens();
        throw new Error("Authentication expired. Please sign in again.");
    }
    const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Try both camelCase and snake_case for backend compatibility
        body: JSON.stringify({
            refreshToken: tokens.refreshToken,
            refresh_token: tokens.refreshToken
        })
    });
    if (!response.ok) {
        clearTokens();
        throw new Error("Authentication expired. Please sign in again.");
    }
    const data = await response.json();
    if (data?.token) {
        // SECURITY: Preserve storage location (session vs local) during refresh
        const normalized = normalizeTokens(data.token);
        persistTokens(normalized, false);
        return normalized;
    }
    throw new Error("Authentication expired. Please sign in again.");
}

export const getAuthHeaders = (extraHeaders = {}) => {
    const tokens = readTokens();
    const headers = { ...extraHeaders };

    if (tokens?.accessToken) {
        headers.Authorization = `Bearer ${tokens.accessToken}`;
    }

    // Include organization context header if set
    if (currentOrgId) {
        headers['X-Org-Id'] = currentOrgId;
    }

    return headers;
};

export async function fetchWithAuth(url, options = {}, retry = true) {
    const headers = getAuthHeaders(options.headers);
    const { skipGlobalAuthHandler, ...fetchOptions } = options;
    const response = await fetch(url, { ...fetchOptions, headers });

    if (response.status === 401 && retry) {
        try {
            await refreshAccessToken();
            return fetchWithAuth(url, { ...options, skipGlobalAuthHandler }, false);
        } catch (refreshError) {
            // Only log if not suppressing global auth errors (reduces noise for expected failures)
            if (!skipGlobalAuthHandler) {
                logError("Token refresh failed:", refreshError);
            }

            // Trigger global authentication error handler
            // Skip if caller explicitly opts out (e.g., during login pre-fetch)
            if (globalAuthErrorHandler && !skipGlobalAuthHandler) {
                console.error("Authentication expired globally. Redirecting to login...");
                // Call the handler asynchronously to avoid blocking
                setTimeout(() => {
                    globalAuthErrorHandler(refreshError);
                }, 100);
            }

            throw refreshError;
        }
    }

    if (!response.ok) {
        logError(`API request failed: ${response.status} ${response.statusText}`, url);
    }

    return response;
}

// Re-export token utilities for convenience
export { clearTokens, persistTokens as setStoredTokens };
